// Persists access + refresh tokens and the authenticated user in localStorage.
// `subscribe(fn)` is called whenever auth state changes so views can react.

const STORAGE_KEY = 'nestly.auth.v1';
// Phase 5 of the role-model rework: the activeRole / canManageProperties
// API still exists for backwards compatibility (callers don't all need
// to change at once) but it no longer gates anything meaningful. Every
// authenticated user can act on properties they own or that they hold
// membership in; the server-side policy is the authority. The role
// chooser screen is dead code reached only from a deep link.

class AuthStore {
  constructor() {
    this._listeners = new Set();
    this._state = this._load();
  }

  get state() { return this._state; }
  get accessToken() { return this._state.accessToken; }
  get refreshToken() { return this._state.refreshToken; }
  get user() { return this._state.user; }
  get isAuthenticated() { return !!this._state.accessToken; }

  get availableRoles() {
    return (this._state.user && this._state.user.roles) || [];
  }

  // Legacy: the multi-role chooser between TENANT / LANDLORD. Now
  // there's no meaningful platform-level choice — gating happens by
  // property/tenancy membership. Kept on the API surface for callers
  // that haven't been migrated yet.
  get activeRole() {
    return this._state.activeRole || null;
  }

  // No longer triggers the chooser screen. The /select-role route is
  // dead — landing there auto-resolves.
  get needsRoleChoice() {
    return false;
  }

  hasAnyRole(...roles) {
    return roles.some((r) => this.availableRoles.includes(r));
  }

  // Who may act as a landlord (see/create properties, the landlord nav +
  // dashboard). Mirrors the server-side gate in PropertyService: anyone who
  // already holds a property, or holds no tenancy at all (brand-new accounts),
  // or has opted into landlord mode. The only group excluded is "pure tenants"
  // — they have a tenancy, no property, and haven't opted in. The server
  // remains the authority; this just keeps the UI honest.
  get canManageProperties() {
    if (!this.isAuthenticated) return false;
    const u = this._state.user || {};
    return !!u.has_properties || !u.has_tenancies || !!u.landlord_enabled;
  }

  get landlordEnabled() {
    return !!(this._state.user && this._state.user.landlord_enabled);
  }

  get marketplaceEnabled() {
    return !!(this._state.user && this._state.user.marketplace_enabled);
  }

  // Subscription plan the auth payload was issued under (e.g. 'pro').
  get planCode() {
    return this._state.user?.plan_code || null;
  }

  // Enabled feature keys from the auth payload, or null when absent.
  get features() {
    return this._state.user?.features || null;
  }

  // Feature gate for nav/routes. Fails OPEN when the session has no
  // `features` field (stale localStorage from before plans shipped) so
  // legacy sessions keep working until the next login/refresh; the
  // server-side 403 remains the authority.
  hasFeature(key) {
    const features = this.features;
    if (!features) return true;
    return features.includes(key);
  }

  patchUser(patch) {
    if (!this._state.user) return;
    this._state = { ...this._state, user: { ...this._state.user, ...patch } };
    this._persist();
    this._emit();
  }

  setActiveRole(role) {
    if (role && !this.availableRoles.includes(role)) return;
    this._state = { ...this._state, activeRole: role || null };
    this._persist();
    this._emit();
  }

  defaultPath() {
    if (!this.isAuthenticated) return '/login';
    // Route by holdings, not platform role: a user who only has
    // tenancies (a pure tenant) lands on /tenancies; everyone else
    // (owners, operators, agency staff, brand-new accounts) lands on
    // the dashboard. The has_* flags come off the auth payload.
    const u = this._state.user || {};
    if (u.has_tenancies && !u.has_properties) return '/tenancies';
    return '/dashboard';
  }

  // Where a brand-new registration lands, by the intent picked on the
  // register form. Landlords go straight into their first property;
  // tenants to their (probably still empty) tenancy list; agency founders
  // to the agency screen where the create-agency CTA lives.
  landingPathForIntent(intent) {
    switch (intent) {
      case 'landlord': return '/properties/new';
      case 'tenant': return '/tenancies';
      case 'agency': return '/agency';
      default: return this.defaultPath();
    }
  }

  // Where an invitation accept lands, by the invitation's kind (from the
  // preview payload). Property invites drop the new member onto the
  // property itself; agency staff onto the agency screen; tenants onto
  // their tenancy list. Anything else falls back to holdings routing.
  landingPathForInvite(preview) {
    const p = preview || {};
    if (p.kind === 'property' && p.target_property_id) return `/properties/${p.target_property_id}`;
    if (p.kind === 'agency') return '/agency';
    if (p.kind === 'tenant') return '/tenancies';
    return this.defaultPath();
  }

  set(payload) {
    const roles = (payload.user && payload.user.roles) || [];
    this._state = {
      accessToken: payload.access_token || payload.accessToken || null,
      refreshToken: payload.refresh_token || payload.refreshToken || null,
      user: payload.user || null,
      // Auto-pick when there is exactly one role; otherwise leave null and let
      // the role chooser screen set it.
      activeRole: roles.length === 1 ? roles[0] : null,
    };
    this._persist();
    this._emit();
  }

  setTokens({ access_token, refresh_token }) {
    this._state = { ...this._state, accessToken: access_token, refreshToken: refresh_token };
    this._persist();
    this._emit();
  }

  clear() {
    this._state = { accessToken: null, refreshToken: null, user: null, activeRole: null };
    this._persist();
    this._emit();
  }

  subscribe(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  _load() {
    const empty = { accessToken: null, refreshToken: null, user: null, activeRole: null };
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return empty;
      const parsed = JSON.parse(raw);
      // Back-fill missing fields for sessions stored before activeRole existed.
      return { activeRole: null, ...parsed };
    } catch (_) {
      return empty;
    }
  }

  _persist() {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this._state)); } catch (_) {}
  }

  _emit() { this._listeners.forEach((fn) => fn(this._state)); }
}

export const auth = new AuthStore();
